import { c as _c } from "react-compiler-runtime";
import { useCallback, useMemo } from 'react';
import PortalIdParser from 'PortalIdParser';
import useObjectTypeId from 'framework-listing-lib/internal/hooks/useObjectTypeId';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import useUserInfo from 'framework-listing-lib/internal/hooks/useUserInfo';
import { useViewProps as useViewPropsContext } from 'framework-listing-lib/internal/providers/ListingProvider';
import listingLibObserver from 'framework-listing-lib/utils/listingLibObserver';
import { useCurrentViewContext } from 'framework-listing-lib/internal/ViewTabs/providers/CurrentViewProvider';
import { useViews as useViewsProvider } from 'framework-listing-lib/internal/ViewTabs/providers/ViewsProvider';
import { getViewId } from 'framework-listing-lib/internal/ViewTabs/utils/views';
const DEFAULT_SAVE_AS_NEW_PROPS = {
  disabled: false
};

/**
 * façade over the context hook in case implementation changes
 */
function useViewProps() {
  "use memo";

  const viewProps = useViewPropsContext();
  return viewProps;
}
export function useHasViews() {
  "use memo";

  const viewProps = useViewProps();
  return Boolean(viewProps && viewProps.hasViews);
}
export function useOnViewIdChange() {
  "use memo";

  const $ = _c(2);
  const viewProps = useViewProps();
  let t0;
  if ($[0] !== viewProps) {
    t0 = (viewId, options) => {
      if (viewProps && viewProps.onViewIdChange) {
        viewProps.onViewIdChange(viewId, options);
      }
      listingLibObserver.setBulkActionSelectionMode("none");
    };
    $[0] = viewProps;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function useViewsNamespace() {
  "use memo";

  const viewProps = useViewProps();
  return viewProps && viewProps.namespace ? viewProps.namespace : "NONE";
}
export function useGetSaveAsNewViewProps() {
  "use memo";

  const $ = _c(3);
  const viewProps = useViewProps();
  const currentView = useCurrentView();
  let t0;
  bb0: {
    if (!currentView || !viewProps || !viewProps.getSaveAsNewViewProps) {
      t0 = DEFAULT_SAVE_AS_NEW_PROPS;
      break bb0;
    }
    let t1;
    if ($[0] !== currentView || $[1] !== viewProps) {
      t1 = viewProps.getSaveAsNewViewProps(currentView);
      $[0] = currentView;
      $[1] = viewProps;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    t0 = t1;
  }
  return t0;
}
export function useRenderTab() {
  "use memo";

  const viewProps = useViewProps();
  return viewProps && viewProps.renderTab ? viewProps.renderTab : undefined;
}
export function useFiltersForNewViews() {
  "use memo";

  const $ = _c(6);
  const viewProps = useViewProps();
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] !== viewProps) {
    t2 = viewProps && viewProps.filtersForNewViews ? viewProps.filtersForNewViews || {} : {};
    $[2] = viewProps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = Object.assign({
      filterGroups: t0,
      quickFilters: t1
    }, t2);
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
export function useViewPropsCurrentViewId() {
  "use memo";

  const viewProps = useViewProps();
  return viewProps && viewProps.currentViewId ? viewProps.currentViewId : undefined;
}
export function useCurrentViewId() {
  "use memo";

  const $ = _c(2);
  const isFoldersMode = useIsFoldersMode();
  const {
    currentView
  } = useCurrentViewContext();
  if (isFoldersMode) {
    return;
  }
  let t0;
  if ($[0] !== currentView) {
    t0 = currentView ? getViewId(currentView) : undefined;
    $[0] = currentView;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
export function useCurrentView() {
  "use memo";

  const isFoldersMode = useIsFoldersMode();
  const {
    currentView
  } = useCurrentViewContext();
  if (isFoldersMode) {
    return;
  }
  return currentView;
}
export function useIsCurrentViewReadOnly() {
  "use memo";

  const currentView = useCurrentView();
  const {
    userInfo
  } = useUserInfo();
  return Boolean(userInfo && currentView && userInfo.user.user_id !== currentView.ownerId);
}
export function useCurrentViewFilters() {
  "use memo";

  const $ = _c(6);
  const currentView = useCurrentView();
  let t0;
  bb0: {
    if (currentView) {
      let t1;
      if ($[0] !== currentView.quickFilters) {
        t1 = currentView.quickFilters || [];
        $[0] = currentView.quickFilters;
        $[1] = t1;
      } else {
        t1 = $[1];
      }
      let t2;
      if ($[2] !== currentView.filterGroups || $[3] !== t1) {
        t2 = {
          filterGroups: currentView.filterGroups,
          quickFilters: t1
        };
        $[2] = currentView.filterGroups;
        $[3] = t1;
        $[4] = t2;
      } else {
        t2 = $[4];
      }
      t0 = t2;
      break bb0;
    }
    let t1;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        filterGroups: [],
        quickFilters: []
      };
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    t0 = t1;
  }
  return t0;
}
export function useViewUpdateState() {
  "use memo";

  const $ = _c(3);
  const {
    viewUpdateState,
    setViewUpdateState
  } = useViewsProvider();
  let t0;
  if ($[0] !== setViewUpdateState || $[1] !== viewUpdateState) {
    t0 = [viewUpdateState, setViewUpdateState];
    $[0] = setViewUpdateState;
    $[1] = viewUpdateState;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
}
export function useViewColumns() {
  "use memo";

  const {
    columns
  } = useViewsProvider();
  return columns;
}
export function useSetViewColumns() {
  "use memo";

  const {
    setColumns
  } = useViewsProvider();
  return setColumns;
}
export function useNumberOfFrozenColumns() {
  "use memo";

  const {
    numberOfFrozenColumns
  } = useViewsProvider();
  return numberOfFrozenColumns;
}
export function useSetNumberOfFrozenColumns() {
  "use memo";

  const {
    setNumberOfFrozenColumns
  } = useViewsProvider();
  return setNumberOfFrozenColumns;
}
export function useSetCardProperties() {
  "use memo";

  const {
    setCardProperties
  } = useViewsProvider();
  return setCardProperties;
}
export function useCardProperties() {
  "use memo";

  const {
    cardProperties
  } = useViewsProvider();
  return cardProperties;
}
export function useQuickFilterPropertyNamesState() {
  "use memo";

  const {
    quickFilterPropertyNames
  } = useViewsProvider();
  return quickFilterPropertyNames;
}
export function useGetAllViewsUrl() {
  "use memo";

  const $ = _c(7);
  const viewProps = useViewProps();
  const objectType = useObjectTypeId();
  const {
    userInfo
  } = useUserInfo();
  const namespace = useViewsNamespace();
  let draftParms;
  if ($[0] !== namespace || $[1] !== objectType || $[2] !== userInfo) {
    draftParms = new URLSearchParams();
    draftParms.append("namespace", namespace);
    draftParms.append("objectTypeId", objectType);
    draftParms.append("returnLink", `${window.location.pathname}${window.location.search}`);
    if (userInfo !== null && userInfo !== void 0 && userInfo.user) {
      draftParms.append("ownerId", `${userInfo.user.user_id}`);
    }
    $[0] = namespace;
    $[1] = objectType;
    $[2] = userInfo;
    $[3] = draftParms;
  } else {
    draftParms = $[3];
  }
  const urlSearchParams = draftParms;
  let t0;
  if ($[4] !== urlSearchParams || $[5] !== viewProps) {
    t0 = viewProps && viewProps.allViewsUrl ? `${viewProps.allViewsUrl}?${urlSearchParams.toString()}` : `/manage-views/${PortalIdParser.get()}/all?${urlSearchParams.toString()}`;
    $[4] = urlSearchParams;
    $[5] = viewProps;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  return t0;
}